import * as React from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { F0TableOfContent, TOCItem } from '@factorialco/f0-react/dist/experimental'
import { MyCommunitiesDocument, OtherCommunitiesDocument } from 'generated/resources/graphql'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { usePaginatedQuery } from 'modules/core/hooks/usePaginatedQuery'
import i18n from 'modules/core/lib/i18n'

type Group = {
  id: number
  title: string
  description?: string | null
  isMember: boolean
}

const CommunitiesSidebar = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const {
    nodes: myCommunitiesNodes,
    controls: {
      fetchMore: fetchMoreMyCommunities,
      hasMore: hasMoreMyCommunities,
      loading: loadingMoreMyCommunities,
    },
  } = usePaginatedQuery(MyCommunitiesDocument, {
    extractConnection: (data) =>
      data.posts?.groupsConnection ?? {
        pageInfo: { hasNextPage: false, endCursor: null },
        edges: [],
      },
  })

  const {
    nodes: otherCommunitiesNodes,
    controls: {
      fetchMore: fetchMoreOtherCommunities,
      hasMore: hasMoreOtherCommunities,
      loading: loadingMoreOtherCommunities,
    },
  } = usePaginatedQuery(OtherCommunitiesDocument, {
    extractConnection: (data) =>
      data.posts?.groupsConnection ?? {
        pageInfo: { hasNextPage: false, endCursor: null },
        edges: [],
      },
  })

  useUnsafeEffect(() => {
    if (hasMoreMyCommunities && !loadingMoreMyCommunities) {
      fetchMoreMyCommunities()
    }
  }, [hasMoreMyCommunities, loadingMoreMyCommunities, fetchMoreMyCommunities])

  useUnsafeEffect(() => {
    if (hasMoreOtherCommunities && !loadingMoreOtherCommunities) {
      fetchMoreOtherCommunities()
    }
  }, [hasMoreOtherCommunities, loadingMoreOtherCommunities, fetchMoreOtherCommunities])

  const myCommunities = React.useMemo<Group[]>(() => {
    return myCommunitiesNodes
  }, [myCommunitiesNodes])

  const otherCommunities = React.useMemo<Group[]>(() => {
    return otherCommunitiesNodes
  }, [otherCommunitiesNodes])

  const selectedGroupId = id ? Number(id) : undefined
  const isFeedSelected = !id
  const activeItemId = isFeedSelected ? 'feed' : selectedGroupId?.toString()

  const handleGroupClick = React.useCallback(
    (groupId: number) => {
      navigate(navigation.communities.id.fullPath({ id: groupId.toString() }))
    },
    [navigate, navigation]
  )

  const handleFeedClick = React.useCallback(() => {
    navigate(navigation.communities.fullPath())
  }, [navigate, navigation])

  const tocItems = React.useMemo(() => {
    const items: TOCItem[] = [
      {
        id: 'feed',
        label: i18n.t('posts.communities.feed'),
        onClick: handleFeedClick,
      },
    ]

    if (myCommunities.length > 0) {
      items.push({
        id: 'my-communities',
        label: i18n.t('posts.communities.my_communities'),
        children: myCommunities.map((group) => ({
          id: group.id.toString(),
          label: group.title ?? '',
          onClick: () => handleGroupClick(group.id),
        })),
      })
    }

    if (otherCommunities.length > 0) {
      items.push({
        id: 'other-communities',
        label: i18n.t('posts.communities.other_communities'),
        children: otherCommunities.map((group) => ({
          id: group.id.toString(),
          label: group.title ?? '',
          onClick: () => handleGroupClick(group.id),
        })),
      })
    }

    return items
  }, [myCommunities, otherCommunities, handleGroupClick, handleFeedClick])

  return (
    <Box
      flexNoShrink
      paddingLeft="s16"
      paddingBottom="s16"
      gap="s12"
      flexDirection="column"
      border={{ right: { style: 'solid', width: 's1', color: 'grey200' } }}
    >
      <Box flexGrow overflowY="auto" overflowX="hidden">
        <F0TableOfContent
          collapsible
          key={JSON.stringify([
            ...myCommunities.map((g) => g.id),
            ...otherCommunities.map((g) => g.id),
          ])}
          activeItem={activeItemId}
          items={tocItems}
          title=""
          showSearchBox
          searchPlaceholder={i18n.t('posts.communities.communities_search_placeholder')}
        />
      </Box>
    </Box>
  )
}

export default CommunitiesSidebar
